'use client';

import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { createContext } from 'react';

import type { Nullable } from '@libs/core';

import type {
    AccountAddressesSuccessResponse,
    AccountAppointmentsSuccessfulResponse,
    AccountPackagesSuccessfulResponse,
    AccountPaymentsSuccessResponse,
} from '../types/account';
import type { Profile } from '../types/profile';
import type { CombinedUserClaims } from '../utils/auth0IdToken';

export type StorageUserSession = {
    firstName?: string;
    basketCount?: number;
    loyaltyPoints?: number;
    isRegistered?: boolean;
};

export type IdentityUser =
    | {
          aud: string;
          customerprofileid: number;
          elevatedauthexpiresat: number;
          exp: number;
          gn: string;
          iss: string;
          janrainaccesstoken: string;
          loyaltyid: number;
          rol: unknown[];
          sid: string;
          sn: string;
          sourcelocation: number;
          oktatoken?: string;
          email?: string;
          scope?: 'remember_me';
      }
    | undefined;

export interface UserData {
    profile: Nullable<Profile>;
    addresses: Nullable<AccountAddressesSuccessResponse[]>;
    payments: Nullable<AccountPaymentsSuccessResponse[]>;
    appointments: Nullable<AccountAppointmentsSuccessfulResponse>;
    packages: Nullable<AccountPackagesSuccessfulResponse[]>;
}

export type UserDataToUpdate =
    | Nullable<Profile>
    | Nullable<AccountAddressesSuccessResponse[]>
    | Nullable<AccountPaymentsSuccessResponse[]>
    | Nullable<AccountAppointmentsSuccessfulResponse>
    | Nullable<AccountPackagesSuccessfulResponse[]>;

export type UserSessionProviderContextProps = {
    isAuthenticated?: 'velocity';
    isAuth0IdTokenUserSynced?: boolean;
    isSoftAuthenticated?: boolean;
    setIsSoftAuthenticated: Dispatch<SetStateAction<boolean>>;
    ogSubscriptionId?: string;
    storageUserSession?: StorageUserSession;
    setUser: Dispatch<SetStateAction<IdentityUser | CombinedUserClaims>>;
    user?: IdentityUser | CombinedUserClaims;
    userData?: Nullable<UserData>;
    updateProfileData: (dataToUpdate?: string) => Promise<void>;
};

type UserSessionProviderProps = PropsWithChildren<{
    values: UserSessionProviderContextProps;
}>;

export const UserSessionProviderContext = createContext<UserSessionProviderContextProps | null>(null);

export function UserSessionProvider({ children, values }: UserSessionProviderProps) {
    return <UserSessionProviderContext.Provider value={values}>{children}</UserSessionProviderContext.Provider>;
}
